import React from 'react';
import { Contenuto } from '../types';

// pubblica.bymarke.com (23/10/2026) segue la conferma del cliente su WhatsApp e
// l'uscita su Metricool e le scrive nel contenuto (conferma_cliente,
// uscita_stato). Qui solo i badge: nessuna azione, si legge e basta.
// «Compatto» per le celle del calendario, pieno per card e modali.

type Tono = 'verde' | 'ambra' | 'rosso' | 'blu';

const TONI: Record<Tono, string> = {
  verde: 'bg-green-100 text-green-700 border-green-200',
  ambra: 'bg-amber-100 text-amber-800 border-amber-200',
  rosso: 'bg-red-100 text-red-700 border-red-200',
  blu: 'bg-blue-100 text-blue-700 border-blue-200',
};

const CONFERMA: Record<string, { tono: Tono; pieno: string; compatto: string; titolo: string }> = {
  confermato: { tono: 'verde', pieno: 'Ok cliente', compatto: 'OK', titolo: 'Il cliente ha confermato il reel' },
  attesa: { tono: 'ambra', pieno: 'Attende ok', compatto: 'ATT', titolo: 'Reel mandato al cliente, si aspetta la conferma' },
  modifiche: { tono: 'rosso', pieno: 'Modifiche', compatto: 'MOD', titolo: 'Il cliente ha chiesto modifiche' },
};

const USCITA: Record<string, { tono: Tono; pieno: string; compatto: string; titolo: string }> = {
  in_coda: { tono: 'blu', pieno: 'Programmato su Metricool', compatto: 'PROG', titolo: 'Programmato su Metricool: esce da solo all\'ora prevista' },
  in_uscita: { tono: 'blu', pieno: 'In uscita', compatto: 'ESCE', titolo: 'Metricool lo sta pubblicando' },
  uscito: { tono: 'verde', pieno: 'Uscito', compatto: 'USC', titolo: 'Uscito su Instagram' },
  errore: { tono: 'rosso', pieno: 'Errore uscita', compatto: 'ERR', titolo: 'Uscita non riuscita' },
  mancato: { tono: 'rosso', pieno: 'Non uscito', compatto: 'NO', titolo: 'Non è uscito alla data prevista' },
};

const classi = (tono: Tono, compatto: boolean) =>
  compatto
    ? `inline-flex items-center px-1 py-0.5 rounded text-[8px] font-semibold flex-shrink-0 whitespace-nowrap border ${TONI[tono]}`
    : `inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold whitespace-nowrap border ${TONI[tono]}`;

export function BadgeConferma({ contenuto, compatto = false }: { contenuto: Contenuto; compatto?: boolean }) {
  const voce = contenuto.confermaCliente ? CONFERMA[contenuto.confermaCliente] : undefined;
  if (!voce) return null;
  const titolo = contenuto.confermaClienteNota ? `${voce.titolo}: ${contenuto.confermaClienteNota}` : voce.titolo;
  return (
    <span className={classi(voce.tono, compatto)} title={`${titolo} (pubblica)`}>
      {compatto ? voce.compatto : voce.pieno}
    </span>
  );
}

export function BadgeUscita({ contenuto, compatto = false }: { contenuto: Contenuto; compatto?: boolean }) {
  const voce = contenuto.uscitaStato ? USCITA[contenuto.uscitaStato] : undefined;
  if (!voce) return null;
  const titolo =
    contenuto.uscitaStato === 'errore' && contenuto.uscitaErrore ? `${voce.titolo}: ${contenuto.uscitaErrore}` : voce.titolo;
  const testo = compatto ? voce.compatto : voce.pieno;
  if (contenuto.uscitaStato === 'uscito' && contenuto.linkPubblicato && !compatto) {
    return (
      <a
        href={contenuto.linkPubblicato}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => e.stopPropagation()}
        className={`${classi(voce.tono, compatto)} underline`}
        title={`${titolo}: apri il post (pubblica)`}
      >
        {testo}
      </a>
    );
  }
  return (
    <span className={classi(voce.tono, compatto)} title={`${titolo} (pubblica)`}>
      {testo}
    </span>
  );
}

/**
 * Solo l'ultimo tag (30/10/2026, il titolare: «voglio solo vedere l'ultimo tag, uscito o
 * programmato»): l'uscita se c'è, se no la conferma del cliente.
 */
export default function BadgePubblica({ contenuto, compatto = false }: { contenuto: Contenuto; compatto?: boolean }) {
  if (contenuto.uscitaStato && USCITA[contenuto.uscitaStato]) return <BadgeUscita contenuto={contenuto} compatto={compatto} />;
  if (contenuto.confermaCliente) return <BadgeConferma contenuto={contenuto} compatto={compatto} />;
  return null;
}

/** C'è un tag di uscita (programmato, uscito, errore…): allora è l'unico tag da mostrare. */
export const haTagUscita = (c: Contenuto) => !!(c.uscitaStato && USCITA[c.uscitaStato]);

/** Mappatura dei campi di pubblica dal contenuto del backend */
export const campiPubblica = (c: any): Partial<Contenuto> => ({
  confermaCliente: c?.conferma_cliente ?? null,
  confermaClienteIl: c?.conferma_cliente_il ? new Date(c.conferma_cliente_il) : undefined,
  confermaClienteNota: c?.conferma_cliente_nota || undefined,
  uscitaStato: c?.uscita_stato ?? null,
  uscitaErrore: c?.uscita_errore || undefined,
});
